import type { RouteRecordRaw } from 'vue-router';

// The requests page is organization-scoped, and the organization lives in the
// path (Linear-style, exactly like `/team/:teamKey/issues`):
// `/org/hc/requests`. `:orgKey` is the organization's short key, lowercased,
// when it has one, its id otherwise (`Organization.key` is nullable).
// `useOrganizationSelection` matches it case-insensitively and rewrites a
// non-canonical spelling (an old `/org/HC/...` link) to the lowercase form.
//
// The scope is in the URL rather than in a `?org=` filter because a request's
// status is a column of ITS organization's board: one organization at a time is
// what gives the surface a single column set to group by and a single status
// filter to offer.
export const organizationRequestsRoute: RouteRecordRaw = {
  path: '/org/:orgKey/requests',
  name: 'OrganizationRequests',
  component: () => import('./pages/RequestsPage.vue'),
  meta: {
    title: 'Requests',
    layout: 'main',
    authorize: ({ isGranted }) => isGranted('dt:core:requests:list'),
  },
};

// A saved view of the organization's requests — the SAME page component as
// `organizationRequestsRoute` on purpose: a view is the requests list with a
// stored filter set and its own preference scope, not a different screen.
// `:slug` is `<kebab-name>-<slugId>`; only the trailing `slugId` is
// authoritative, so a rename keeps old links working.
//
// Deliberately NOT an organization-SWITCH target (see `ORGANIZATION_SCOPED_ROUTES`
// in `useOrganizationSelection`): switching organizations while inside a view
// would keep the `:slug` of a view belonging to the organization just left.
export const requestsViewRoute: RouteRecordRaw = {
  path: '/org/:orgKey/views/:slug',
  name: 'RequestsView',
  component: () => import('./pages/RequestsPage.vue'),
  meta: {
    title: 'Requests',
    layout: 'main',
    authorize: ({ isGranted }) => isGranted('dt:core:requests:list'),
  },
};

// Bare `/requests` (sidebar link, `{ name: 'Requests' }` navigations, every
// bookmark and `?org=` link from before the surface was scoped) resolves to the
// organization-scoped URL. A static redirect can't do this — the organization
// list is fetched async — so a resolver page picks one and replaces to it (same
// pattern as `IssuesRedirectPage`).
export const requestsRoute: RouteRecordRaw = {
  path: '/requests',
  name: 'Requests',
  component: () => import('./pages/RequestsRedirectPage.vue'),
  meta: {
    title: 'Requests',
    layout: 'main',
    authorize: ({ isGranted }) => isGranted('dt:core:requests:list'),
  },
};

// Legacy `/requests/views/:slug`, from before the surface was organization-
// scoped. Same resolver page: it forwards to the scoped URL keeping the slug,
// so an old bookmark still opens its view — under the organization the user was
// last in, which is the one that view belongs to in every case but a shared link
// pasted across organizations (there the view simply reads as not found, the
// same as any view of a scope you are not in).
export const requestsViewRedirectRoute: RouteRecordRaw = {
  path: '/requests/views/:slug',
  name: 'RequestsViewRedirect',
  component: () => import('./pages/RequestsRedirectPage.vue'),
  meta: {
    title: 'Requests',
    layout: 'main',
    authorize: ({ isGranted }) => isGranted('dt:core:requests:list'),
  },
};

export const requestRoute: RouteRecordRaw = {
  // By the human-friendly global number, not the entity id — "#42" → /requests/42.
  // Deliberately NOT organization-scoped: the number is unique across every
  // board, and a request's own page needs no column set to render.
  path: '/requests/:number(\\d+)',
  name: 'Request',
  component: () => import('./pages/RequestPage.vue'),
  meta: {
    title: 'Request',
    layout: 'main',
    authorize: ({ isGranted }) => isGranted('dt:core:requests:get'),
  },
};
